import { Flex, Text } from '@chakra-ui/react';
import { IDependentValue } from '@publishing-engine/domain/models';
import { isDependentValue } from 'src/utils/displayHelpers';
import { convertDataToLabel } from 'src/pages/MyTask/Assessments/hooks';

interface DisplayFieldProps {
  fieldName: string;
  value: string | IDependentValue<string>;
  config?: any;
  showAdditionalDetails?: boolean;
}

/**
 * Reusable component to display form fields with support for both string and IDependentValue types.
 * Handles "Additional Details" rendering when value is a dependent value with exists=true.
 */
export const DisplayField: React.FC<DisplayFieldProps> = ({
  fieldName,
  value,
  config,
  showAdditionalDetails = true
}) => {
  if (!config || !config[fieldName]) {
    return null;
  }

  // Handle IDependentValue type
  if (isDependentValue(value)) {
    const labels = config[fieldName]?.params.labels;
    const sectionLabel =
      value.exists === false && labels?.sectionNo
        ? labels.sectionNo.title
        : labels?.section?.title ?? 'Additional Details';

    return (
      <Flex flexDirection="column" gap={2}>
        <Text textStyle="headingItem">
          {labels?.main}
        </Text>
        <Text textStyle="textBody">{value.exists ? 'Yes' : 'No'}</Text>
        {showAdditionalDetails && value.value && (
          <Flex flexDirection="column">
            <Text textStyle="headingItem">{sectionLabel}</Text>
            <Text textStyle="textBody">{value.value}</Text>
          </Flex>
        )}
      </Flex>
    );
  }

  // Handle string value
  if (typeof value === 'string' && value) {
    return (
      <Flex flexDirection="column" gap={2}>
        <Text textStyle="headingItem">
          {config[fieldName]?.params.labels.main}
        </Text>
        <Text textStyle="textBody">{convertDataToLabel(value)}</Text>
      </Flex>
    );
  }

  return null;
};

interface DisplayFieldFromFormProps<T extends Record<string, any>> {
  formValues: T;
  formConfigs: any;
  configField: string;
  valueField: keyof T;
  showOptionLabel?: boolean;
}

/**
 * Component that extracts value from formValues and displays it with config lookup.
 * Used for option-based fields where the value is a key into config options.
 */
export const DisplayFieldFromForm = <T extends Record<string, any>>({
  formValues,
  formConfigs,
  configField,
  valueField,
  showOptionLabel = true
}: DisplayFieldFromFormProps<T>): React.ReactElement | null => {
  const config = formConfigs?.[configField];
  const rawValue = formValues?.[valueField];
  // Handle IDependentValue type
  if (isDependentValue(rawValue)) {
    const dependentValue = rawValue as IDependentValue<string>;
    const depLabels = config.params.labels;
    const depSectionLabel =
      dependentValue.exists === false && depLabels?.sectionNo
        ? depLabels.sectionNo.title
        : depLabels?.section?.title ?? 'Additional Details';

    return (
      <Flex flexDirection="column" gap={2}>
        <Text textStyle="headingItem">{depLabels.main}</Text>
        <Text textStyle="textBody">{dependentValue.exists ? 'Yes' : 'No'}</Text>
        {dependentValue.value && (
          <Flex flexDirection="column">
            <Text textStyle="headingItem">{depSectionLabel}</Text>
            <Text textStyle="textBody">{dependentValue.value}</Text>
          </Flex>
        )}
      </Flex>
    );
  }

  // Handle string value with option lookup
  if (showOptionLabel && typeof rawValue === 'string') {
    const label = config?.params?.labels?.main;
    const answer = config?.params?.labels?.options[rawValue]?.main;

    return (
      <>
        {label && answer && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">{label}</Text>
            <Text textStyle="textBody">{answer}</Text>
          </Flex>
        )}
      </>
    );
  }

  return null;
};
